import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useNavigationType, useSearchParams } from 'react-router-dom';
import { Icon } from '@/components/ui/Icon';
import { Button } from '@/components/ui/Button';
import { LogoImage } from '@/components/ui/LogoImage';
import { ObservationCard } from '@/components/observation/ObservationCard';
import { isObservationVisibleToViewer } from '@/utils/observationDisplay';
import { MatchReactionBar } from '@/components/match/MatchReactionBar';
import { FanZonePanel } from '@/components/match/FanZonePanel';
import { fffMatchClassementUrl, fffMatchSheetUrl, getMatchEpreuvesKey } from '@/constants/Config';
import { Colors } from '@/constants/Colors';
import { useTheme } from '@/hooks/useTheme';
import { apiService } from '@/services/api';
import { useFavoritesStore } from '@/stores/favoritesStore';
import { useObservationStore } from '@/stores/observationStore';
import { useClubStore } from '@/stores/clubStore';
import { useProfileStore } from '@/stores/profileStore';
import { formatObservationCategoryBadge } from '@/utils/competitionLabel';
import { formatJourneeShort } from '@/utils/matchLabel';
import { enrichObservationWithMatch } from '@/utils/enrichObservationWithMatch';
import { matchConnexionCle } from '@/utils/favoriteKeys';
import { getMatchObservationBlockMessage } from '@/utils/matchKickoff';
import { trackUsageEvent } from '@/services/usageEvent';
import {
  buildMatchShareUrl,
  shareContent,
  shareResultToFeedback,
} from '@/utils/shareLink';
import type { Match } from '@/types';

const MATCH_TABS = [
  { id: 'resume', label: 'Résumé' },
  { id: 'observations', label: 'Observations' },
  { id: 'fanzone', label: 'Fan zone' },
  { id: 'avis', label: 'Avis' },
] as const;

type MatchTabId = (typeof MATCH_TABS)[number]['id'];

function parseMatchTab(raw: string | null): MatchTabId {
  if (raw === 'observations' || raw === 'fanzone' || raw === 'avis' || raw === 'resume') return raw;
  return 'resume';
}

function InfoRow({ icon, label, value, colors }: { icon: string; label: string; value: string; colors: any }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 12, paddingBottom: 12, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Icon name={icon} size={18} color={colors.textSecondary} />
        <span style={{ fontSize: 14, color: colors.textSecondary }}>{label}</span>
      </div>
      <span style={{ fontSize: 14, fontWeight: 600, color: colors.text }}>{value}</span>
    </div>
  );
}

function InfoRowClassementLink({ icon, label, href, colors }: { icon: string; label: string; href: string; colors: any }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 12, paddingBottom: 12, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Icon name={icon} size={18} color={colors.textSecondary} />
        <span style={{ fontSize: 14, color: colors.textSecondary }}>{label}</span>
      </div>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: Colors.primary,
          textDecoration: 'none',
          padding: '6px 14px',
          borderRadius: 12,
          border: `1px solid ${Colors.primary}40`,
          backgroundColor: `${Colors.primary}10`,
        }}
      >
        Voir
      </a>
    </div>
  );
}

function findClubByName<T extends { nom: string }>(clubs: T[], teamName: string | undefined): T | undefined {
  if (!teamName) return undefined;
  const target = teamName.toLowerCase().trim();
  if (!target) return undefined;
  const exact = clubs.find((c) => (c.nom || '').toLowerCase().trim() === target);
  if (exact) return exact;
  return clubs.find((c) => {
    const n = (c.nom || '').toLowerCase().trim();
    return n && (n.includes(target) || target.includes(n));
  });
}

/** Ex. « U14 R1 - Poule A » ; si la poule commence déjà par « Poule », on ne la préfixe pas deux fois. */
function formatCompetitionWithPoule(competition?: string, poule?: string): string {
  const c = (competition ?? '').trim();
  const p = (poule ?? '').trim();
  if (!p) return c;
  const poolPart = /^poule\b/i.test(p) ? p : `Poule ${p}`;
  if (!c) return poolPart;
  return `${c} - ${poolPart}`;
}

export default function MatchDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const colors = useTheme();
  const [match, setMatch] = useState<Match | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shareFeedback, setShareFeedback] = useState<string | null>(null);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const activeTab = parseMatchTab(searchParams.get('tab'));

  const setActiveTab = useCallback(
    (tab: MatchTabId) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (tab === 'resume') next.delete('tab');
          else next.set('tab', tab);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  useEffect(() => {
    const timerId = window.setInterval(() => setNowTick(Date.now()), 30_000);
    return () => window.clearInterval(timerId);
  }, []);

  const observationBlockMessage = useMemo(() => {
    if (!match) return null;
    return getMatchObservationBlockMessage(match, new Date(nowTick));
  }, [match, nowTick]);

  const { load: loadFavorites, isMatchFavorite, toggleMatch } = useFavoritesStore();
  const tifosiCle = useProfileStore((s) => s.tifosiCle);
  const { byMatch, fetchByMatch } = useObservationStore();
  const { clubs, fetchClubs } = useClubStore();
  const navigationType = useNavigationType();
  const latestMatchRouteIdRef = useRef<string | undefined>(undefined);
  latestMatchRouteIdRef.current = id;

  const observationsKey = useMemo(() => {
    if (!match) return '';
    return String(match.cle || match.idmatch_football || id || '').trim();
  }, [match, id]);

  const loadMatch = useCallback(async () => {
    if (!id) return;
    const requestId = id;
    setIsLoading(true);
    setError(null);
    try {
      const result = await apiService.getMatchById(requestId);
      if (latestMatchRouteIdRef.current !== requestId) return;
      if (result?.data) setMatch(result.data);
      else setError('Impossible de charger ce match. Vérifiez la connexion ou réessayez.');
    } catch {
      if (latestMatchRouteIdRef.current !== requestId) return;
      setError('Impossible de charger les détails du match');
    } finally {
      if (latestMatchRouteIdRef.current === requestId) setIsLoading(false);
    }
  }, [id]);

  useEffect(() => { loadFavorites(); }, [loadFavorites]);
  useEffect(() => {
    void loadMatch();
  }, [id, loadMatch]);
  /** Enregistrement GoPaaS `tifosi_event` (match_opened) — profil connecté uniquement ; pas sur retour historique (`POP`) ; pas après retour depuis fiche observation ; déduplication courte (Strict Mode). */
  useEffect(() => {
    if (navigationType === 'POP') return;
    if (!match || !tifosiCle) return;
    const matchKey = String(match.cle || match.idmatch_football || id || '').trim();
    if (!matchKey) return;
    try {
      const raw = sessionStorage.getItem(`gopaas_skip_match_opened:${tifosiCle}`);
      if (raw) {
        const parsed = JSON.parse(raw) as { ids?: string[]; exp?: number };
        const exp = typeof parsed?.exp === 'number' ? parsed.exp : 0;
        const ids = Array.isArray(parsed?.ids) ? parsed.ids.map((x) => String(x).trim()).filter(Boolean) : [];
        if (Date.now() < exp && ids.length > 0) {
          const candidates = new Set(
            [id, match.cle, match.idmatch_football].filter(Boolean).map((x) => String(x).trim()),
          );
          if (ids.some((mid) => candidates.has(mid))) {
            return;
          }
        }
        if (Date.now() >= exp) {
          sessionStorage.removeItem(`gopaas_skip_match_opened:${tifosiCle}`);
        }
      }
    } catch {
      try {
        sessionStorage.removeItem(`gopaas_skip_match_opened:${tifosiCle}`);
      } catch {
        /* ignore */
      }
    }
    trackUsageEvent(
      { type: 'match_opened', tifosi: tifosiCle, match_football: matchKey },
      { navigationType, dedupeContext: matchKey },
    );
  }, [match, tifosiCle, id, navigationType]);
  useEffect(() => { if (observationsKey) fetchByMatch(observationsKey); }, [observationsKey, fetchByMatch]);
  useEffect(() => { if (clubs.length === 0) fetchClubs(); }, [clubs.length, fetchClubs]);

  const matchObservations = useMemo(() => {
    const observations = observationsKey ? byMatch[observationsKey] ?? [] : [];
    return [...observations]
      .filter((obs) => isObservationVisibleToViewer(obs, tifosiCle))
      .sort((a, b) => {
        const ta = a.date ? new Date(a.date).getTime() : 0;
        const tb = b.date ? new Date(b.date).getTime() : 0;
        return tb - ta;
      });
  }, [byMatch, observationsKey, tifosiCle]);

  const formatDate = (dateStr: string | undefined): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const openDirections = () => {
    let destination: string;
    if (match?.latitude && match?.longitude) {
      destination = `${match.latitude},${match.longitude}`;
    } else {
      const parts = [match?.terrain || match?.nom || match?.stade || '', match?.cp, match?.ville].filter(Boolean).join(', ');
      if (!parts.trim()) return;
      destination = encodeURIComponent(parts);
    }
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${destination}`, '_blank');
  };

  const handleShare = async () => {
    if (!match) return;
    const matchId = match.idmatch_football || match.cle || id || '';
    const url = buildMatchShareUrl(matchId);
    const title = `${match.homeTeam} vs ${match.awayTeam}`;
    const comp = formatCompetitionWithPoule(match.competition, match.poule) || match.competition || 'Match';
    const text = `${title} — ${comp} le ${formatDate(match.date)}`;
    const result = await shareContent({ title, text, url });
    const feedback = shareResultToFeedback(result);
    if (feedback) setShareFeedback(feedback);
    window.setTimeout(() => setShareFeedback(null), 2200);
  };

  const matchFavoriteCle = matchConnexionCle(match);
  const isFavorite = matchFavoriteCle
    ? isMatchFavorite(matchFavoriteCle)
    : id
      ? isMatchFavorite(id)
      : false;
  const handleToggleFavorite = () => {
    if (!matchFavoriteCle) return;
    toggleMatch(matchFavoriteCle);
  };

  const homeClub = useMemo(() => findClubByName(clubs, match?.homeTeam), [clubs, match?.homeTeam]);
  const awayClub = useMemo(() => findClubByName(clubs, match?.awayTeam), [clubs, match?.awayTeam]);

  const logoShellStyle: React.CSSProperties = {
    width: 80,
    height: 80,
    borderRadius: 16,
    padding: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: `${colors.background}80`,
    border: `1px solid ${Colors.primary}30`,
    boxSizing: 'border-box',
  };

  const handleAddObservation = () => {
    if (!match || observationBlockMessage) return;
    navigate('/observation/detailed', {
      state: {
        matchId: match.cle || match.idmatch_football || id,
        matchRouteId: id,
        matchLatitude: match.latitude,
        matchLongitude: match.longitude,
        homeTeam: match.homeTeam,
        awayTeam: match.awayTeam,
        homeLogo: match.home_logo,
        awayLogo: match.away_logo,
        homeClubId: homeClub?.idclub,
        awayClubId: awayClub?.idclub,
        homeClubCle: homeClub?.cle,
        awayClubCle: awayClub?.cle,
        matchCompetition: match.competition?.trim() || undefined,
        matchPoule: match.poule?.trim() || undefined,
      },
    });
  };

  const openObservation = (observation: (typeof matchObservations)[number]) => {
    if (!match) return;
    navigate('/observation/detailed', {
      state: {
        mode: 'edit',
        observation,
        matchId: match.cle || match.idmatch_football || id,
        matchRouteId: id,
        matchLatitude: match.latitude,
        matchLongitude: match.longitude,
        homeTeam: match.homeTeam,
        awayTeam: match.awayTeam,
        homeLogo: match.home_logo,
        awayLogo: match.away_logo,
        homeClubId: homeClub?.idclub,
        awayClubId: awayClub?.idclub,
        homeClubCle: homeClub?.cle,
        awayClubCle: awayClub?.cle,
        matchCompetition: match.competition?.trim() || undefined,
        matchPoule: match.poule?.trim() || undefined,
      },
    });
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.background }}>
        <div className="spinner" />
      </div>
    );
  }

  if (error || !match) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, backgroundColor: colors.background }}>
        <PageHeader navigate={navigate} colors={colors} isFavorite={false} onToggleFavorite={() => {}} onShare={() => {}} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, gap: 16 }}>
          <Icon name="alert-circle" size={48} color={Colors.primary} />
          <span style={{ fontSize: 14, fontWeight: 500, color: colors.text, textAlign: 'center' }}>{error}</span>
          <Button title="Réessayer" onPress={loadMatch} />
        </div>
      </div>
    );
  }

  const time = match.heure ? match.heure.slice(0, 5) : '--:--';
  const hasScore = match.home_score !== null && match.away_score !== null;
  const epreuvesKey = getMatchEpreuvesKey(match);
  const competitionLine = formatCompetitionWithPoule(match.competition, match.poule);
  const fanZoneMatchId = String(match.cle || match.idmatch_football || id || '').trim();
  const contentPaddingBottom = activeTab === 'observations' ? 96 : activeTab === 'fanzone' ? 16 : 24;
  const pinFanZoneLayout = activeTab === 'fanzone';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, backgroundColor: colors.background }}>
      <PageHeader navigate={navigate} colors={colors} isFavorite={isFavorite} onToggleFavorite={handleToggleFavorite} onShare={handleShare} />
      {shareFeedback && (
        <div style={{ padding: '6px 16px', backgroundColor: `${Colors.primary}15`, color: Colors.primary, fontSize: 12, fontWeight: 600, textAlign: 'center' }}>
          {shareFeedback}
        </div>
      )}

      <div
        style={{
          flex: 1,
          overflowY: pinFanZoneLayout ? 'hidden' : 'auto',
          padding: 16,
          paddingBottom: contentPaddingBottom,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <div
          style={{
            borderRadius: 16,
            border: `1px solid ${Colors.primary}30`,
            overflow: 'hidden',
            marginBottom: 12,
            flexShrink: 0,
          }}
        >
          <div style={{ padding: 24, backgroundColor: `${Colors.primary}10` }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                {homeClub?.idclub ? (
                  <button
                    type="button"
                    onClick={() => navigate(`/club/${homeClub.idclub}`)}
                    aria-label={`Voir la fiche club ${match.homeTeam}`}
                    style={{ ...logoShellStyle, cursor: 'pointer' }}
                  >
                    <LogoImage uri={match.home_logo} size={64} />
                  </button>
                ) : (
                  <div style={logoShellStyle}>
                    <LogoImage uri={match.home_logo} size={64} />
                  </div>
                )}
                <span style={{ fontSize: 14, fontWeight: 700, textAlign: 'center', color: colors.text }}>{match.homeTeam}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0 8px' }}>
                <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4, color: Colors.primary }}>{hasScore ? 'Score final' : "Coup d'envoi"}</span>
                {hasScore ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 36, fontWeight: 900, color: colors.text }}>{match.home_score}</span>
                    <span style={{ fontSize: 24, color: `${Colors.primary}60` }}>-</span>
                    <span style={{ fontSize: 36, fontWeight: 900, color: colors.text }}>{match.away_score}</span>
                  </div>
                ) : (
                  <span style={{ fontSize: 28, fontWeight: 900, color: colors.text }}>{time}</span>
                )}
                <span
                  style={{
                    fontSize: 10,
                    marginTop: 8,
                    color: colors.textSecondary,
                    textAlign: 'center',
                    maxWidth: 140,
                    lineHeight: 1.35,
                  }}
                >
                  {competitionLine || match.competition || '-'}
                </span>
              </div>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                {awayClub?.idclub ? (
                  <button
                    type="button"
                    onClick={() => navigate(`/club/${awayClub.idclub}`)}
                    aria-label={`Voir la fiche club ${match.awayTeam}`}
                    style={{ ...logoShellStyle, cursor: 'pointer' }}
                  >
                    <LogoImage uri={match.away_logo} size={64} />
                  </button>
                ) : (
                  <div style={logoShellStyle}>
                    <LogoImage uri={match.away_logo} size={64} />
                  </div>
                )}
                <span style={{ fontSize: 14, fontWeight: 700, textAlign: 'center', color: colors.text }}>{match.awayTeam}</span>
              </div>
            </div>
            {hasScore && epreuvesKey ? (
              <a
                href={fffMatchSheetUrl(epreuvesKey)}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  marginTop: 16,
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  color: Colors.primary,
                  textDecoration: 'none',
                }}
              >
                <Icon name="document-outline" size={16} color={Colors.primary} />
                Feuille de match
              </a>
            ) : null}
          </div>
        </div>

        <div
          role="tablist"
          aria-label="Sections du match"
          style={{ display: 'flex', borderBottom: `1px solid ${Colors.primary}15`, marginBottom: 16, flexShrink: 0, overflowX: 'auto' }}
        >
          {MATCH_TABS.map((tab) => {
            const isActive = activeTab === tab.id;
            const count = tab.id === 'observations' ? matchObservations.length : undefined;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  flex: 1,
                  minWidth: 0,
                  padding: '12px 4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 4,
                  background: 'transparent',
                  border: 'none',
                  borderBottomWidth: 2,
                  borderBottomStyle: 'solid',
                  borderBottomColor: isActive ? Colors.primary : 'transparent',
                  cursor: 'pointer',
                }}
              >
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: isActive ? Colors.primary : colors.textSecondary,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {tab.label}
                </span>
                {typeof count === 'number' ? (
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      minWidth: 18,
                      height: 18,
                      padding: '0 5px',
                      borderRadius: 9,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: isActive ? Colors.primary : `${Colors.primary}20`,
                      color: isActive ? '#ffffff' : Colors.primary,
                    }}
                  >
                    {count}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>

        {activeTab === 'resume' ? (
          <>
            <div
              style={{
                borderRadius: 16,
                border: `1px solid ${Colors.primary}30`,
                overflow: 'hidden',
                marginBottom: 16,
              }}
            >
              <MatchReactionBar
                match={match}
                matchKey={observationsKey}
                tifosiCle={tifosiCle}
                variant="toolbar"
              />
            </div>
            <div style={{ ...styles.infoCard, backgroundColor: colors.surface, borderColor: colors.border }}>
              <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, marginBottom: 12, color: colors.textSecondary }}>STADE</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 48, height: 48, borderRadius: 12, backgroundColor: `${Colors.primary}10`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon name="football" size={24} color={Colors.primary} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: colors.text }}>{match.terrain || match.nom || match.stade || '-'}</div>
                  <div style={{ fontSize: 12, marginTop: 2, color: colors.textSecondary }}>{[match.cp, match.ville].filter(Boolean).join(' ')}</div>
                </div>
                <button onClick={openDirections} style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: `${Colors.primary}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', border: 'none' }}>
                  <Icon name="navigate-outline" size={22} color={Colors.primary} />
                </button>
              </div>
            </div>

            <div style={{ ...styles.infoCard, backgroundColor: colors.surface, borderColor: colors.border }}>
              <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, marginBottom: 12, color: colors.textSecondary }}>INFORMATIONS</span>
              <InfoRow icon="calendar-outline" label="Date" value={formatDate(match.date)} colors={colors} />
              <InfoRow icon="flag-outline" label="Journée" value={formatJourneeShort(match.journee)} colors={colors} />
              <InfoRow icon="leaf-outline" label="Surface" value={match.surface || 'Non spécifié'} colors={colors} />
              {epreuvesKey ? (
                <InfoRowClassementLink
                  icon="analytics-outline"
                  label="Classement"
                  href={fffMatchClassementUrl(epreuvesKey)}
                  colors={colors}
                />
              ) : null}
            </div>
          </>
        ) : null}

        {activeTab === 'observations' ? (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: colors.text }}>Observations</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: Colors.primary }}>
                  {matchObservations.length} au total
                </span>
                <button
                  type="button"
                  onClick={() => { if (observationsKey) fetchByMatch(observationsKey); }}
                  aria-label="Actualiser les observations"
                  style={{ width: 32, height: 32, borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: colors.surface, border: `1px solid ${colors.border}`, cursor: 'pointer' }}
                >
                  <Icon name="refresh" size={16} color={colors.text} />
                </button>
              </div>
            </div>
            {matchObservations.length === 0 ? (
              <div style={{ padding: 24, borderRadius: 16, border: `1px dashed ${colors.border}`, textAlign: 'center', backgroundColor: colors.surface }}>
                <Icon name="analytics-outline" size={32} color={colors.textSecondary} />
                <div style={{ fontSize: 13, color: colors.textSecondary, marginTop: 8 }}>
                  Aucune observation pour ce match
                </div>
              </div>
            ) : (
              matchObservations.map((obs) => {
                const enriched = enrichObservationWithMatch(obs, match);
                const categoryLabel = formatObservationCategoryBadge(
                  match.competition,
                  match.poule,
                );
                return (
                  <ObservationCard
                    key={obs.id}
                    observation={enriched}
                    categoryLabel={categoryLabel}
                    showMatchInfo={false}
                    onPress={() => openObservation(enriched)}
                  />
                );
              })
            )}
          </div>
        ) : null}

        {activeTab === 'fanzone' && fanZoneMatchId ? (
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <FanZonePanel matchId={fanZoneMatchId} />
          </div>
        ) : null}

        {activeTab === 'avis' ? (
          <div style={{ padding: 32, borderRadius: 16, border: `1px dashed ${colors.border}`, textAlign: 'center', backgroundColor: colors.surface }}>
            <Icon name="star-outline" size={36} color={colors.textSecondary} />
            <div style={{ fontSize: 16, fontWeight: 700, color: colors.text, marginTop: 12 }}>
              Avis du match
            </div>
            <div style={{ fontSize: 13, color: colors.textSecondary, marginTop: 8, lineHeight: 1.4 }}>
              Bientôt disponible — notez le match et partagez votre ressenti.
            </div>
          </div>
        ) : null}
      </div>

      {activeTab === 'observations' ? (
        <ObservationsFooter
          onAddObservation={handleAddObservation}
          observationBlockMessage={observationBlockMessage}
          colors={colors}
        />
      ) : null}
    </div>
  );
}

function ObservationsFooter({
  onAddObservation,
  observationBlockMessage,
  colors,
}: {
  onAddObservation: () => void;
  observationBlockMessage: string | null;
  colors: any;
}) {
  const observationDisabled = Boolean(observationBlockMessage);
  return (
    <div
      style={{
        position: 'sticky',
        bottom: 0,
        zIndex: 5,
        padding: '10px 16px 14px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        borderTop: `1px solid ${Colors.primary}20`,
        background: `linear-gradient(to top, ${colors.background} 85%, ${colors.background}E6 100%)`,
      }}
    >
      {observationBlockMessage ? (
        <p style={{ margin: 0, fontSize: 12, lineHeight: 1.4, color: colors.textSecondary, textAlign: 'center' }}>
          {observationBlockMessage}
        </p>
      ) : null}
      <button
        type="button"
        onClick={onAddObservation}
        disabled={observationDisabled}
        aria-disabled={observationDisabled}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          minHeight: 44,
          padding: '10px 12px',
          borderRadius: 16,
          border: 'none',
          backgroundColor: observationDisabled ? `${Colors.primary}55` : Colors.primary,
          cursor: observationDisabled ? 'not-allowed' : 'pointer',
          opacity: observationDisabled ? 0.85 : 1,
        }}
      >
        <Icon name="person-add" size={18} color="#ffffff" />
        <span style={{ color: '#ffffff', fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap' }}>Ajouter observation</span>
      </button>
    </div>
  );
}

function PageHeader({
  navigate,
  colors,
  isFavorite,
  onToggleFavorite,
  onShare,
}: {
  navigate: any;
  colors: any;
  isFavorite: boolean;
  onToggleFavorite: () => void;
  onShare: () => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: `1px solid ${Colors.primary}15` }}>
      <button onClick={() => navigate(-1)} style={{ width: 40, height: 40, borderRadius: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', border: 'none', background: 'transparent' }}>
        <Icon name="arrow-back" size={24} color={colors.text} />
      </button>
      <span style={{ fontSize: 18, fontWeight: 700, color: colors.text }}>Détails du match</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <button
          onClick={onToggleFavorite}
          aria-label={isFavorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}
          style={{ width: 40, height: 40, borderRadius: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: 'transparent', border: 'none' }}
        >
          <Icon name={isFavorite ? 'star' : 'star-outline'} size={24} color={isFavorite ? Colors.primary : colors.text} />
        </button>
        <button
          onClick={onShare}
          aria-label="Partager"
          style={{ width: 40, height: 40, borderRadius: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: 'transparent', border: 'none' }}
        >
          <Icon name="share-outline" size={24} color={colors.text} />
        </button>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  infoCard: { borderRadius: 16, padding: 16, border: '1px solid', marginBottom: 16, display: 'flex', flexDirection: 'column' },
};
